import { Box, Typography } from "@mui/material";
import { useActiveDatasetIds, useChatStore } from "./stores/ChatStore";

import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import { type SendMessageOptions } from "src/reusable/Chat/ChatStreaming/stores/CoreStreamingSlice";
import AtomAssistPageLoad from "./components/AtomAssistPageLoad";
import PersonaNotFoundNotice from "./components/PersonaNotFoundNotice";
import AASplashScreen from "./components/splashScreen/AASplashScreen";
import useAssistantNavigation from "./hooks/useAssistantNavigation";
import { useSyncPersonaToStore } from "./hooks/useSyncPersonaToStore";
import useTalkFirst from "./hooks/useTalkFirst";

import { AtomAssistSplashLocationState, ImageContent } from "./types";
import useDefaultModelCapabilities from "./hooks/useDefaultModelCapabilities";
import { useSetAtomAssistConfig } from "./hooks/useProductConfig";
import { initiateOnboarding } from "./services/OnboardingService";
import { useOnboardingStore } from "./stores/OnboardingStore";

const AASplashPage = () => {
  const history = useHistory();

  // ✅ Use new ChatStore
  const {
    config: chatConfig,
    persona,
    setDropdownDatasets,
    setProjectDatasets,
    setActiveDialogueId,
    setInitialUserMessage,
    uploadedFiles,
    clearUploadedFiles,
    isChatReady,
    setSourceDataRecordIds,
    addUploadedFile,
    sourceDataRecordIds,
    setRouting,
    setHasTalkedFirst,
  } = useChatStore();

  // ✅ Get active dataset IDs from computed selector
  const activeDatasetIds = useActiveDatasetIds();

  // ✅ Load default model capabilities (GeneralLLM) for vision support detection
  useDefaultModelCapabilities();

  // Set product configuration for Atom Assist
  useSetAtomAssistConfig();

  // Handle persona resolution with URL sync enabled for splash page
  const {
    isLoading: isSyncingPersona,
    isError,
    personaNotFound,
    updateUrlPersona,
  } = useSyncPersonaToStore({
    allowUrlSync: true,
    ignoreDialogueHistory: true, // Always start fresh on splash page
  });

  const { navigateToChat } = useAssistantNavigation();
  const location = useLocation<AtomAssistSplashLocationState>();
  const initialUploadDialogueId = location.state?.initialUploadDialogueId;
  const preloadDocument = location.state?.preloadDocument;

  // Track if we've processed preloadDocument to avoid re-processing
  const preloadDocProcessedRef = useRef(false);

  // Reset the ref on fresh mount (when there's no preloadDocument in state)
  // This handles returning to splash after a previous "Chat with Document" session
  useEffect(() => {
    if (!preloadDocument) {
      preloadDocProcessedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // ✅ Handle "Chat with Document" navigation - set up document context
  useEffect(() => {
    if (preloadDocument && !preloadDocProcessedRef.current) {
      preloadDocProcessedRef.current = true;

      // Set the source data record IDs for document-scoped search
      setSourceDataRecordIds([preloadDocument.sdrId]);

      // Add the document to uploadedFiles for UI display (shows as attachment chip)
      addUploadedFile({
        id: `sdr-${preloadDocument.sdrId}`,
        file: {
          name: preloadDocument.fileName,
          size: 0,
          type: "application/pdf",
        } as File,
        datasetId: preloadDocument.datasetId,
        sourceDataRecordId: preloadDocument.sdrId,
        uploadStatus: "uploaded",
        uploadProgress: 100,
        processingStatus: "completed",
        processingProgress: 100,
      });

      // Clear the location state to prevent re-processing on navigation
      history.replace({ pathname: location.pathname, state: undefined });
    }
  }, [
    preloadDocument,
    setSourceDataRecordIds,
    addUploadedFile,
    history,
    location.pathname,
  ]);

  const {
    mutateAsync: createDialogue,
    isLoading: isCreatingDialogue,
    isSuccess: isDialogueCreated,
    error: dialogueError,
    isError: isDialogueError,
  } = useCreateDialogue();

  const {
    isLoading: isInitiateTalkFirstLoading,
    isSuccess: isInitiateTalkFirstSuccess,
    isError: isInitiateTalkFirstError,
    error: initiateTalkFirstError,
  } = useTalkFirst();

  const status = {
    isLoading: isCreatingDialogue || isInitiateTalkFirstLoading,
    isSuccess: isDialogueCreated && isInitiateTalkFirstSuccess,
    isError: isDialogueError || isInitiateTalkFirstError,
    error: dialogueError || initiateTalkFirstError,
  };

  const handleSubmit = async (
    userInput: string,
    images?: ImageContent[],
    opts?: SendMessageOptions,
  ) => {
    // Unauthorized and unconfigured workflows proceed through talk-first
    // with pending system prompts — no need to block here.

    const chatIsReady = isChatReady();

    if (!chatIsReady || !chatConfig) {
      throw new Error(
        "Chat is not ready. Please wait for configuration to load.",
      );
    }

    // Ensure complete persona config before submission to prevent cutoff messages
    // Default persona has chatConfigId=null (so backend runs routing), which is valid
    const hasPersonaInStore = Boolean(persona);
    const hasCompletePersonaConfig =
      Boolean(chatConfig.chatConfigId && persona?.slug) ||
      Boolean(persona?.slug && !chatConfig.chatConfigId);

    if (hasPersonaInStore && !hasCompletePersonaConfig) {
      throw new Error(
        "Agent configuration is still loading. Please wait a moment.",
      );
    }

    // Captured now and re-applied (locked) by navigateToChat; locking here
    // would leave a failed createDialogue with a dead toggle on the splash.
    const responseMode = useChatStore.getState().responseMode;

    if (initialUploadDialogueId) {
      navigateToChat({
        dialogueId: initialUploadDialogueId,
        options: {
          persona: persona?.slug ? persona.slug : undefined,
          config: chatConfig,
          responseMode,
          userMessage: userInput,
          sendId: opts?.sendId,
          uploadedFiles: uploadedFiles, // Pass uploaded files to the chat page
          images: images, // Pass VLM images to the chat page
          sourceDataRecordIds: sourceDataRecordIds, // Pass SDR IDs for document-scoped search
        },
      });
      return Promise.resolve(true);
    }

    // Create updated config that includes ad-hoc uploaded datasets
    // EXCLUDE SDR files - they use sourceDataRecordIds for scoping, not dataset search
    const configWithAllDatasets = {
      ...chatConfig,
      datasets: [
        ...(chatConfig.datasets || []),
        ...uploadedFiles
          .filter((f) => f.datasetId && !f.sourceDataRecordId) // Exclude SDR files
          .map((f) => ({ id: f.datasetId! })),
      ],
    };

    const conversationPayload = {
      model_id: chatConfig.modelId ?? null, // Can be null - backend defaults to GeneralLLM
      model_version_number: chatConfig.modelVersionNum ?? null, // Can be null
      search_datasets: activeDatasetIds, // Use computed dataset IDs
      n_search_results: chatConfig.topSearchResults,
      model_inference_data: {
        infer_data: JSON.stringify([]),
        stream: true,
        streaming_response_required: true,
        text_gen_model_params: chatConfig.chatControlMetadata,
      },
      tool_calling: true,
      chat_config_id: chatConfig.chatConfigId,
      product: "atom-assist",
    };

    await createDialogue(
      {
        conversation: conversationPayload,
      },
      {
        onSuccess: (dialogueId: number) => {
          navigateToChat({
            dialogueId,
            options: {
              persona: persona?.slug ? persona.slug : undefined,
              config: configWithAllDatasets, // Pass config with all datasets
              responseMode,
              userMessage: userInput,
              sendId: opts?.sendId,
              uploadedFiles: uploadedFiles, // Pass uploaded files to the chat page
              images: images, // Pass VLM images to the chat page
              sourceDataRecordIds: sourceDataRecordIds, // Pass SDR IDs for document-scoped search
            },
          });
        },
      },
    );

    return Promise.resolve(true);
  };

  useEffect(() => {
    // A new conversation starts unlocked at the Thorough default. Mount-only:
    // the effect below re-runs on any location.state change, and a splash
    // file upload rewrites state (initialUploadDialogueId), which must not
    // discard the mode the user just picked.
    useChatStore.getState().resetResponseMode();
  }, []);

  useEffect(() => {
    // Reset chat state when mounting the splash page (navigating to /assistant)
    setActiveDialogueId(null); // Clear active dialogue ID
    setInitialUserMessage(undefined); // Clear any previous initial message
    // Clear routing from previous conversation (routing is per-conversation state).
    // Don't clear persona here — useSyncPersonaToStore owns persona lifecycle and
    // clearing here races with its effect (mount effect runs after sync effect,
    // undoing the persona set on cached data).
    setRouting(null);
    setHasTalkedFirst(false); // Reset so useTalkFirst can fire on next workflow click

    // Clear workflow state based on URL: if ?persona= is absent, user navigated
    // back to the clean splash page and all workflow state should reset.
    // If ?persona= is present, WorkflowNavigation just set workflowPersona
    // and useTalkFirst needs it — don't clear.
    const searchParams = new URLSearchParams(location.search);
    const hasPersonaInUrl = Boolean(searchParams.get("persona"));
    const { workflowPersona: currentWP, forceAutoStart: currentForce } =
      useChatStore.getState();
    if (!hasPersonaInUrl || (!currentWP && !currentForce)) {
      useChatStore.getState().setPersonaForWorkflow(null);
    }

    // ALWAYS clear uploaded files when returning to splash screen
    // Files should only persist during active dialogue sessions
    // Exception: if we have preloaded data from navigation buttons OR already processed preloadDocument
    const hasPreloadedData =
      location.state?.preloadDataset ||
      location.state?.preloadDatasets ||
      location.state?.preloadDocument;

    // Also check if we already processed a preloadDocument (state was cleared but ref is set)
    const alreadyProcessedPreloadDoc = preloadDocProcessedRef.current;

    if (
      !hasPreloadedData &&
      !alreadyProcessedPreloadDoc &&
      uploadedFiles.length > 0
    ) {
      clearUploadedFiles();
    }

    setProjectDatasets([]); // Clear project datasets from previous project session

    // This effect should run whenever we navigate to the splash page
    // location.search included so re-triggers when ?persona= changes on same page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.state]);

  // Handle preloaded dataset(s) from navigation (e.g., "Chat with Dataset" or "Chat with Folder" buttons)
  useEffect(() => {
    // Only run after persona sync is complete to avoid race conditions
    if (!isSyncingPersona) {
      const preloadDataset = location.state?.preloadDataset;
      const preloadDatasets = location.state?.preloadDatasets;

      if (preloadDatasets && preloadDatasets.length > 0) {
        setDropdownDatasets(preloadDatasets);
      } else if (preloadDataset) {
        setDropdownDatasets([preloadDataset]);
      }
    }
  }, [
    isSyncingPersona,
    location.state?.preloadDataset,
    location.state?.preloadDatasets,
    setDropdownDatasets,
  ]);

  // FTUX Onboarding gate: single atomic server-side decision.
  //
  // Previous iterations of this gate stacked multiple async dependencies
  // (onboarding-status query + persona sync + chat config ready) and a ref
  // to avoid double-fire, which produced intermittent no-ops. The new
  // approach calls a single endpoint that decides "onboarding needed" AND
  // creates the dialogue server-side in one transaction. The frontend just
  // navigates to whatever dialogue_id comes back.
  const { setOnboardingDialogueId } = useOnboardingStore();
  const [isInitiatingOnboarding, setIsInitiatingOnboarding] = useState(true);
  const onboardingCheckedRef = useRef(false);

  useEffect(() => {
    if (onboardingCheckedRef.current) return;
    onboardingCheckedRef.current = true;

    initiateOnboarding()
      .then(({ dialogue_id }) => {
        if (dialogue_id) {
          setOnboardingDialogueId(dialogue_id);
          navigateToChat({
            dialogueId: dialogue_id,
            options: {
              persona: "ftux-onboarding",
              userMessage: "", // empty message triggers session start
            },
          });
        } else {
          setIsInitiatingOnboarding(false);
        }
      })
      .catch((err) => {
        console.error("[FTUX] initiate-onboarding failed", err);
        onboardingCheckedRef.current = false;
        setIsInitiatingOnboarding(false);
      });
  }, [navigateToChat, setOnboardingDialogueId]);

  // Show loading while deciding whether to show splash or redirect to FTUX
  if (isSyncingPersona || isInitiatingOnboarding) {
    return <AtomAssistPageLoad />;
  }

  if (isError) {
    return (
      <Box sx={{ textAlign: "center", p: 2 }}>
        <Typography variant="h6" color="error">
          Failed to load assistant configuration. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <AASplashScreen
      data-testid="aa-splash-page"
      chatStreamError={status.error as any}
      isChatConnectionError={status.isError}
      isChatConnected={false}
      isChatLoading={status.isLoading}
      isChatEnded={!status.isLoading}
      terminateConnection={() => {}}
      sendUserMessage={handleSubmit}
      notice={
        <PersonaNotFoundNotice
          personaNotFound={personaNotFound}
          clearUrlPersona={() => updateUrlPersona(null)}
        />
      }
    />
  );
};

export default AASplashPage;
